import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { css, cx, cache } from '@emotion/css';
export { keyframes as unsafe_keyframes } from '@emotion/css';
import { useContext, createContext, useMemo, memo, forwardRef, version, cloneElement, useLayoutEffect as useLayoutEffect$1, useState, useEffect, useCallback, useRef, useInsertionEffect, useSyncExternalStore } from 'react';
import { Overlay as Overlay$1, usePopover as usePopover$1 } from '@react-aria/overlays';
import { useIsSSR } from '@react-aria/ssr';
import { I18nProvider } from '@react-aria/i18n';
import { useToastRegion, useToast } from '@react-aria/toast';
import { createPortal } from 'react-dom';
import { insertStyles } from '@emotion/utils';
import { serializeStyles } from '@emotion/serialize';
import { StyleSheet } from '@emotion/sheet';
import { ToastQueue } from '@react-stately/toast';
import { FocusableProvider as FocusableProvider$1, useFocusable as useFocusable$1, useFocusRing } from '@react-aria/focus';
import { mergeProps as mergeProps$1, useObjectRef as useObjectRef$1, useResizeObserver as useResizeObserver$1, useLayoutEffect as useLayoutEffect$2, isIOS, chain, getScrollParent } from '@react-aria/utils';
import { useHover, usePress } from '@react-aria/interactions';

const TokensContext = /*#__PURE__*/ createContext({
    theme: undefined,
    colorScheme: 'light'
});
const TokensProvider = ({ children, theme, colorScheme })=>{
    const contextValue = useMemo(()=>({
            theme,
            colorScheme
        }), [
        colorScheme,
        theme
    ]);
    return /*#__PURE__*/ jsx(TokensContext.Provider, {
        value: contextValue,
        children: children
    });
};
/**
 * Hook to access token values tied to the configured theme
 * @returns Token object providing raw style values
 */ const useTokens = ()=>{
    const tokensContext = useContext(TokensContext);
    if (!tokensContext.theme) {
        throw new Error('The `TokensProvider` has not been instantiated correctly with its corresponding context. Please make sure that `GeminiProvider` is rendered higher in your app with a `theme` assigned on it. Also, please be sure to use `useTokens` under a `GeminiProvider`. If the issue persists, report it to Team GEMINI.');
    }
    return tokensContext.theme[tokensContext.colorScheme];
};
const useColorScheme = ()=>{
    const tokensContext = useContext(TokensContext);
    return tokensContext.colorScheme;
};

const typography = (value)=>value;
const paddingVertical = (value)=>({
        paddingTop: value,
        paddingBottom: value
    });
const paddingHorizontal = (value)=>({
        paddingLeft: value,
        paddingRight: value
    });
const marginVertical = (value)=>({
        marginTop: value,
        marginBottom: value
    });
const marginHorizontal = (value)=>({
        marginLeft: value,
        marginRight: value
    });
const placeholderColor = (value)=>({
        '::placeholder': {
            color: value,
            /**
		 * Our sanitizing use color:inherit with an opacity
		 * We remove opacity and force our color
		 */ opacity: 1
        }
    });
const scrollbarDisplay = (value)=>({
        '::-webkit-scrollbar': {
            display: value
        }
    });
const srOnly = (value)=>value ? {
        position: 'absolute',
        left: '-10000px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden'
    } : {};
/**
 * @todo: To be deprecated pending addition of `mediaRules`
 */ const reducedMotion = (value)=>value ? {
        '@media(prefers-reduced-motion: reduce)': {
            animation: 'none'
        }
    } : {};
const customStyles = {
    typography,
    paddingVertical,
    paddingHorizontal,
    marginVertical,
    marginHorizontal,
    placeholderColor,
    reducedMotion,
    scrollbarDisplay,
    srOnly
};
const customStylesKeys = Object.keys(customStyles);

const referencePrefix = 'reference.';
/**
 * Generate className by mapping style properties to the underlying styling technology
 * @param props Style properties to process
 * @param tokens Tokens object to retrieve raw CSS values
 * @returns Generated class name
 */ const createClassName = (props, tokens)=>{
    const propKeys = Object.keys(props);
    const hasStyleProps = propKeys.length > 0;
    if (!hasStyleProps) return undefined;
    return css(mapStylePropsToCSSObject(props, tokens, propKeys));
};
/**
 * Map style properties to the underlying styling library
 * @param props Style properties to process
 * @param tokens Tokens object to retrieve raw CSS values
 * @param propKeys Computed keys for `props` (to prevent re-computing for performance purposes)
 * @returns Mapped output for the underlying styling library
 */ const mapStylePropsToCSSObject = (props, tokens, propKeys)=>{
    const output = {};
    const breakpointsQueries = {};
    const keys = propKeys || Object.keys(props);
    for (const key of keys){
        const propValue = props[key];
        if (!isResponsiveObject(propValue)) {
            appendStyleValue(output, key, getStyleValue(propValue, tokens));
            continue;
        }
        for (const breakpointKey of Object.keys(propValue)){
            const breakpointValue = propValue[breakpointKey];
            if (breakpointValue === undefined) continue;
            const breakpointStyleValue = getStyleValueFromToken(tokens, breakpointKey);
            const styleValue = getStyleValue(breakpointValue, tokens);
            // If breakpoint is mobile (min-width: 0px), skip usage of media-query
            if (breakpointStyleValue === 0) {
                appendStyleValue(output, key, styleValue);
                continue;
            }
            const mediaQueryValue = breakpointsQueries[breakpointStyleValue] ?? {};
            appendStyleValue(mediaQueryValue, key, styleValue);
            breakpointsQueries[breakpointStyleValue] = mediaQueryValue;
        }
    }
    // Take breakpoint numbers (Object.keys gives string anyway)
    let breakpointKeys = Object.keys(breakpointsQueries).map(Number);
    // Order only if 2 breakpoints or more
    if (breakpointKeys.length > 1) {
        breakpointKeys = breakpointKeys.sort((a, b)=>a - b);
    }
    // Apply ordered breakpoints
    breakpointKeys.forEach((breakpoint)=>{
        output[`@media(min-width: ${breakpoint}px)`] = breakpointsQueries[breakpoint];
    });
    return output;
};
/**
 * Split properties between style and rest ones
 * @param props Props to split
 * @returns Array of split properties
 */ const splitStyleProps = (props)=>{
    const styleProps = {};
    const restProps = {};
    for (const key of Object.keys(props)){
        const value = props[key];
        if (isStyleProp(key)) {
            styleProps[key] = value;
        } else {
            restProps[key] = value;
        }
    }
    return [
        styleProps,
        restProps
    ];
};
const isStyleProp = (key)=>{
    return STYLE_KEYS[key];
};
const getStyleValueFromToken = (tokens, token)=>{
    let value = tokens[getAbsoluteStringValue(token)];
    const isNegativeLikeToken = token.startsWith('-');
    if (typeof value === 'string' && value.startsWith(referencePrefix)) {
        value = tokens[value.slice(referencePrefix.length, value.length)];
    }
    if (typeof value !== 'string') {
        return isNegativeLikeToken ? -value : value;
    }
    return `${isNegativeLikeToken ? '-' : ''}${value}`;
};
const getAbsoluteStringValue = (value)=>{
    return value.startsWith('-') ? value.slice(1) : value;
};
const isToken = (key, tokens)=>{
    if (typeof key !== 'string') return false;
    const tokenLike = getAbsoluteStringValue(key);
    return Object.prototype.hasOwnProperty.call(tokens, tokenLike);
};
const isResponsiveObject = (value)=>{
    if (value === undefined) return false;
    return typeof value === 'object' && !Array.isArray(value) && value !== null;
};
/**
 * Tests whether a value is an array.
 *
 * @internal
 */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
function isArray(value) {
    // See: https://github.com/microsoft/TypeScript/issues/17002
    return Array.isArray(value);
}
/**
 * Compute value/tokens (should not be responsive at this point, only values) to return values without tokens
 */ const getStyleValue = (value, tokens)=>{
    // Fallback array
    if (isArray(value)) {
        return value.map((elt)=>isToken(elt, tokens) ? getStyleValueFromToken(tokens, elt) : elt);
    }
    if (isToken(value, tokens)) {
        return getStyleValueFromToken(tokens, value);
    }
    return value;
};
const appendStyleValue = (objectToAppend, key, value)=>{
    if (value === undefined) return;
    if (customStylesKeys.includes(key)) {
        const customStyle = customStyles[key](value);
        for (const customStyleKey of Object.keys(customStyle)){
            objectToAppend[customStyleKey] = customStyle[customStyleKey];
        }
    } else {
        objectToAppend[key] = value;
    }
};
// Object is used instead of an array for type safety (stricter shape check thanks to object literal)
// (eg. Array-like type cannot enforce all `StyleProps` key reference)
const STYLE_KEYS = {
    alignContent: true,
    alignItems: true,
    alignSelf: true,
    animationName: true,
    animationDelay: true,
    animationDirection: true,
    animationIterationCount: true,
    animationFillMode: true,
    animationPlayState: true,
    animationDuration: true,
    animationTimingFunction: true,
    backgroundClip: true,
    backgroundColor: true,
    backgroundImage: true,
    backgroundRepeat: true,
    backgroundSize: true,
    backgroundOrigin: true,
    backgroundPosition: true,
    backgroundAttachment: true,
    borderBottomColor: true,
    borderBottomLeftRadius: true,
    borderBottomRightRadius: true,
    borderBottomStyle: true,
    borderBottomWidth: true,
    borderColor: true,
    borderLeftColor: true,
    borderLeftStyle: true,
    borderLeftWidth: true,
    borderRadius: true,
    borderRightColor: true,
    borderRightStyle: true,
    borderRightWidth: true,
    borderStyle: true,
    borderTopColor: true,
    borderTopLeftRadius: true,
    borderTopRightRadius: true,
    borderTopStyle: true,
    borderTopWidth: true,
    borderWidth: true,
    outlineWidth: true,
    outlineStyle: true,
    outlineColor: true,
    outlineOffset: true,
    bottom: true,
    boxShadow: true,
    color: true,
    columnGap: true,
    clipPath: true,
    cursor: true,
    display: true,
    flex: true,
    flexBasis: true,
    flexDirection: true,
    flexGrow: true,
    flexShrink: true,
    flexWrap: true,
    gap: true,
    gridArea: true,
    gridAutoColumns: true,
    gridAutoFlow: true,
    gridAutoRows: true,
    gridColumn: true,
    gridColumnEnd: true,
    gridColumnStart: true,
    gridRow: true,
    gridRowEnd: true,
    gridRowStart: true,
    gridTemplateAreas: true,
    gridTemplateColumns: true,
    gridTemplateRows: true,
    height: true,
    justifyContent: true,
    justifyItems: true,
    justifySelf: true,
    placeItems: true,
    placeContent: true,
    left: true,
    listStyleType: true,
    margin: true,
    marginBottom: true,
    marginHorizontal: true,
    marginLeft: true,
    marginRight: true,
    marginTop: true,
    marginVertical: true,
    maxHeight: true,
    maxWidth: true,
    minHeight: true,
    minWidth: true,
    objectFit: true,
    objectPosition: true,
    opacity: true,
    order: true,
    overflow: true,
    overflowWrap: true,
    overflowX: true,
    overflowY: true,
    padding: true,
    paddingBottom: true,
    paddingHorizontal: true,
    paddingLeft: true,
    paddingRight: true,
    paddingTop: true,
    paddingVertical: true,
    placeholderColor: true,
    pointerEvents: true,
    position: true,
    reducedMotion: true,
    right: true,
    rowGap: true,
    scrollbarWidth: true,
    scrollbarDisplay: true,
    scrollBehavior: true,
    srOnly: true,
    textAlign: true,
    textOverflow: true,
    textShadow: true,
    textTransform: true,
    top: true,
    transform: true,
    transformOrigin: true,
    transitionDelay: true,
    transitionProperty: true,
    transitionDuration: true,
    transitionTimingFunction: true,
    touchAction: true,
    typography: true,
    userSelect: true,
    verticalAlign: true,
    visibility: true,
    whiteSpace: true,
    width: true,
    wordBreak: true,
    zIndex: true,
    resize: true
};

const DEFAULT_AS_ELEMENT = 'div';

const createBox = (render)=>{
    return /*#__PURE__*/ memo(/*#__PURE__*/ forwardRef(render));
};
/**
 * `useBox` handles all the logic to transform Box properties to HTML attributes.
 * To prevent opening the Pandora's box, it must be only used internally in `@gemini/core`.
 * With that in mind, this hook *must* not be exported externally. Other Gemini libraries must only rely on `Box` component
 * or on abstractions hiding the `useBox` usage (for example, via an exposed core component or hook using it internally)
 * @param Box properties
 * @returns HTML attributes
 */ const useBox = ({ as, children, testId, unsafe_className, unsafe_style, ...props })=>{
    const tokens = useTokens();
    const [styleProps, restProps] = splitStyleProps(props);
    const className = createClassName(styleProps, tokens);
    return {
        ...restProps,
        children,
        className: cx(className, unsafe_className) || undefined,
        style: unsafe_style,
        'data-testid': testId
    };
};
/**
 * Temporary function to handle the inert attribute in React 17/18/19
 * @TODO: GEMINI-2713 To be removed when we drop support for React 18/17
 */ const getMappedInertAttribute = (inert, isNewReact)=>isNewReact ? typeof inert === 'string' ? true : inert : typeof inert === 'boolean' ? inert ? '' : undefined : inert;
const Box = createBox((props, ref)=>{
    const { as: Component = DEFAULT_AS_ELEMENT } = props;
    // @TODO: GEMINI-2713 put back `inert` in `htmlProps` when we drop support for React 18/17
    const { inert, ...htmlProps } = useBox(props);
    const isNewReact = useMemo(()=>parseInt(version, 10) >= 19, []);
    return /*#__PURE__*/ jsx(Component, {
        ...htmlProps,
        // @TODO: GEMINI-2713 remove this condition when we drop support for React 18/17
        // @ts-ignore inert is not a valid attribute in React 17/18
        inert: getMappedInertAttribute(inert, isNewReact),
        ref: ref
    });
});

const Icon = /*#__PURE__*/ forwardRef(({ children, /**
			 * Provides a human-readable title for the element that contains it.
			 * https://www.w3.org/TR/SVG-access/#Equivalent
			 */ 'aria-label': ariaLabel, size = 'sizing.24', color = 'inherit', ...restProps }, ref)=>{
    const tokens = useTokens();
    const cssSize = size === 'inherit' ? '1em' : tokens[`unsafe_component.icon.${size}`];
    return /*#__PURE__*/ jsxs(Box, {
        ...restProps,
        as: "svg",
        ref: ref,
        "aria-hidden": ariaLabel ? undefined : true,
        color: color,
        flex: "none",
        unsafe_className: css({
            fill: 'currentcolor',
            width: cssSize,
            height: cssSize
        }),
        children: [
            ariaLabel && /*#__PURE__*/ jsx("title", {
                children: ariaLabel
            }),
            children
        ]
    });
});

const AssetsContext = /*#__PURE__*/ createContext({
    assetsBaseUrl: undefined
});
const AssetsProvider = ({ assetsBaseUrl, children })=>{
    const contextValue = useMemo(()=>({
            assetsBaseUrl
        }), [
        assetsBaseUrl
    ]);
    return /*#__PURE__*/ jsx(AssetsContext.Provider, {
        value: contextValue,
        children: children
    });
};
const useAssetsBaseUrl = ()=>{
    const context = useContext(AssetsContext);
    if (!context) {
        throw new Error('The `assetsBaseUrl` has not been instantiated correctly with its corresponding context. Please make sure that `GeminiProvider` is rendered higher in your app with a `BrandURL` assigned on it. If the issue persists, report it to Team GEMINI.');
    }
    // Default fallback to relative root path ("/"), so if domain is https://www.immowelt.de => https://www.immowelt.de/
    return context.assetsBaseUrl ?? '/';
};

const illustrationAssetsBaseUrl = '/assets/gemini/illustrations/v1';
const joinURL = (...parts)=>{
    return parts.map((part)=>part.replace(/(^\/+|\/+$)/g, '')).join('/');
};
const combineURL = (baseUrl, colorScheme, src)=>{
    return joinURL(baseUrl, illustrationAssetsBaseUrl, colorScheme, src);
};
const Illustration = ({ src, size, responsiveSrc, 'aria-label': ariaLabel, ...restProps })=>{
    const assetsBaseUrl = useAssetsBaseUrl();
    const baseUrl = assetsBaseUrl.replace(/\/$/, '');
    const colorScheme = useColorScheme();
    const combinedSrc = combineURL(baseUrl, colorScheme, src);
    const transformedSize = useMemo(()=>parseInt(size), [
        size
    ]);
    const formattedSrcSet = responsiveSrc.map((src)=>combineURL(baseUrl, colorScheme, src)).join(', ');
    return /*#__PURE__*/ jsx(Box, {
        ...restProps,
        as: "img",
        src: combinedSrc,
        srcSet: formattedSrcSet,
        alt: ariaLabel ?? '',
        "aria-hidden": ariaLabel ? undefined : true,
        flex: "none",
        minWidth: transformedSize,
        width: transformedSize,
        height: "auto"
    });
};

/**
 * Returns whether the component is currently being server side rendered or on the client.
 * Can be used to delay browser-specific rendering until after hydration.
 * @param awaitHydration will await for hydration to be done before returning true
 */ const useIsServer = (awaitHydration = false)=>{
    // Previous version of `useIsServer` wasn't dealing great with hydration and since `useIsSSR` has fixed issues, we can reuse it
    // Fixed issue: https://github.com/adobe/react-spectrum/issues/3787#issuecomment-1363013404
    const isSSR = useIsSSR();
    const isDOM = typeof window !== 'undefined' && window.document && window.document.documentElement;
    return awaitHydration ? isSSR : !isDOM;
};
/**
 * An internal hook to run server-only logic. Its main use case is to collect critical elements that need to be processed server-side (see. `ServerProvider.data`).
 * A critical element is a React element that is required to be rendered as soon as possible in the Critical Rendering Path (CRP).
 * This hook is prefixed with `unsafe` due to its internal nature: it **must** not be exposed to the outside.
 * @see `@gemini/server`: collected critical elements are rendered globally in the application head.
 * @returns Server methods
 */ const useUnsafeServerContext = ()=>{
    const isServer = useIsServer();
    const serverManager = useContext(ServerContext);
    if (!isServer) {
        // no-op client-side
        return null;
    }
    return serverManager;
};
const ServerContext = /*#__PURE__*/ createContext({
    collectCriticalElement () {}
});
const ServerProvider = ({ children })=>{
    const contextValue = useMemo(()=>{
        return {
            collectCriticalElement (id, element) {
                ServerProvider.data[id] = /*#__PURE__*/ cloneElement(element, {
                    key: id,
                    'data-gemini': id
                });
            }
        };
    }, []);
    return /*#__PURE__*/ jsx(ServerContext.Provider, {
        value: contextValue,
        children: children
    });
};
ServerProvider.data = {};

// eslint-disable-next-line no-restricted-syntax
const useLayoutEffect = (...args)=>{
    const isServer = useIsServer();
    const effect = isServer ? /* No-op function server-side*/ ()=>{} : useLayoutEffect$1;
    return effect(...args);
};

const ZIndexContext = /*#__PURE__*/ createContext(undefined);
const ZIndexContextProvider = ({ children, zIndex })=>{
    const tokens = useTokens();
    const contextualZIndex = useZIndexContext({
        unsafe_dismissWarning: true
    }) || tokens['zIndex.none']; // Retrieve computed `zIndex` from upper ZIndexContextProvider in the React tree
    const computedZIndex = contextualZIndex + tokens[zIndex];
    return /*#__PURE__*/ jsx(ZIndexContext.Provider, {
        value: computedZIndex,
        children: children
    });
};
const useZIndexContext = ({ unsafe_dismissWarning } = {})=>{
    const contextZIndex = useContext(ZIndexContext);
    if (process.env.NODE_ENV !== 'production' && !unsafe_dismissWarning && contextZIndex === undefined) {
        console.warn('You should use an Overlay around the content of any custom popover/modal to make `useZIndexContext` works correctly');
    }
    return useContext(ZIndexContext);
};

const OverlayContext = /*#__PURE__*/ createContext(null);
const OverlayProvider = ({ children, scopingSelector })=>{
    const isServer = useIsServer(true);
    const defaultScopingElement = isServer ? null : document.body;
    const [scopeElement, setScopeElement] = useState(defaultScopingElement);
    useLayoutEffect(()=>{
        if (isServer || !scopingSelector) return;
        setScopeElement(document.querySelector(scopingSelector) ?? document.body);
    }, [
        isServer,
        scopingSelector
    ]);
    return /*#__PURE__*/ jsx(OverlayContext.Provider, {
        value: scopeElement,
        children: children
    });
};
const Overlay = ({ children, isOpen, isExiting = false, zIndex = 'zIndex.1' })=>{
    const scopeElement = useContext(OverlayContext);
    if (!isOpen && !isExiting) return null;
    return /*#__PURE__*/ jsx(ZIndexContextProvider, {
        zIndex: zIndex,
        children: /*#__PURE__*/ jsx(Overlay$1, {
            ...scopeElement ? {
                portalContainer: scopeElement
            } : {},
            children: children
        })
    });
};

const DEFAULT_LOCALE = 'en-GB';
const LocaleContext = /*#__PURE__*/ createContext(DEFAULT_LOCALE);
const LocaleProvider = ({ children, locale })=>{
    return /*#__PURE__*/ jsx(LocaleContext.Provider, {
        value: locale,
        children: /*#__PURE__*/ jsx(I18nProvider, {
            locale: locale,
            children: children
        })
    });
};
const useLocale = ()=>{
    return useContext(LocaleContext);
};

const DefaultRenderLink = ({ isExternal, ...restProps }, ref)=>{
    // eslint-disable-next-line no-restricted-syntax
    return /*#__PURE__*/ jsx("a", {
        ...restProps,
        ref: ref
    });
};
const RenderLinkContext = /*#__PURE__*/ createContext(DefaultRenderLink);
const RenderLinkProvider = ({ renderLink = DefaultRenderLink, children })=>{
    return /*#__PURE__*/ jsx(RenderLinkContext.Provider, {
        value: renderLink,
        children: children
    });
};
/**
 * `useRenderLink` retrieves the `renderLink` setting from `GeminiProvider`
 * and returns a link component accepting `BoxProps<'a'>`
 * @returns `LinkComponent`
 */ const useRenderLink = (options)=>{
    return useMemo(()=>{
        return createLinkComponent({
            isExternal: options.isExternal
        });
    }, [
        options.isExternal
    ]);
};
const createLinkComponent = (options)=>/*#__PURE__*/ forwardRef(function LinkComponent(props, ref) {
        const renderLink = useContext(RenderLinkContext);
        const htmlProps = useBox(props);
        return renderLink({
            ...htmlProps,
            isExternal: options.isExternal
        }, ref);
    });

const BreakpointContext = /*#__PURE__*/ createContext(null);
const breakpointPrefix = 'breakpoint.';
const BreakpointProvider = ({ children })=>{
    const tokens = useTokens();
    const orderedBreakpoints = Object.entries(tokens).filter(([key])=>key.startsWith('breakpoint.')).sort((a, b)=>a[1] - b[1]).map(([key])=>key.slice(breakpointPrefix.length, key.length));
    const supportsMatchMedia = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
    const getBreakpointHandler = ()=>{
        const matched = [];
        for (const breakpoint of orderedBreakpoints){
            const query = `(min-width: ${tokens[`breakpoint.${breakpoint}`]}px)`;
            if (window.matchMedia(query).matches) {
                matched.push(breakpoint);
            }
        }
        return matched;
    };
    const [breakpoint, setBreakpoint] = useState(()=>supportsMatchMedia ? getBreakpointHandler() : [
            'base'
        ]);
    useEffect(()=>{
        if (!supportsMatchMedia) {
            return;
        }
        const onResize = ()=>{
            const breakpointHandler = getBreakpointHandler();
            setBreakpoint((previousBreakpointHandler)=>{
                if (previousBreakpointHandler.length !== breakpointHandler.length || previousBreakpointHandler.some((breakpoint, idx)=>breakpoint !== breakpointHandler[idx])) {
                    return [
                        ...breakpointHandler
                    ]; // Return a new array to force state change
                }
                return previousBreakpointHandler;
            });
        };
        window.addEventListener('resize', onResize);
        return ()=>{
            window.removeEventListener('resize', onResize);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        supportsMatchMedia
    ]);
    // If in SSR, the media query should never match. Once the page hydrates,
    // this will update and the real value will be returned.
    const isServer = useIsServer(true);
    return /*#__PURE__*/ jsx(BreakpointContext.Provider, {
        value: {
            currentBreakpoint: isServer ? 'base' : breakpoint[breakpoint.length - 1],
            orderedBreakpoints
        },
        children: children
    });
};
function useBreakpointValue(props, { fallback } = {}) {
    const breakpointContext = useContext(BreakpointContext);
    // If in SSR, the media query should never match.
    const isServer = useIsServer(true);
    // If there is no context or we are on SSR, use fallback value or `base`
    if (!breakpointContext || isServer) {
        return props[fallback || 'base'];
    }
    const { currentBreakpoint, orderedBreakpoints } = breakpointContext;
    let index = Object.keys(props).indexOf(currentBreakpoint);
    if (index !== -1) {
        return props[currentBreakpoint];
    }
    let stopIndex = orderedBreakpoints.indexOf(currentBreakpoint);
    while(stopIndex >= 0){
        const key = orderedBreakpoints[stopIndex];
        if (props[key] !== undefined) {
            index = stopIndex;
            break;
        }
        stopIndex -= 1;
    }
    if (index !== -1) {
        const key = orderedBreakpoints[index];
        return props[key];
    }
    return props[fallback || 'base'];
}

const DEFAULT_SAFE_AREA = {
    top: undefined,
    bottom: undefined,
    left: undefined,
    right: undefined
};
const SafeAreaContext = /*#__PURE__*/ createContext({
    safeArea: DEFAULT_SAFE_AREA,
    setSafeArea: ()=>{},
    resetSafeArea: ()=>{}
});
const SafeAreaProvider = ({ children })=>{
    const [safeArea, setSafeArea] = useState(DEFAULT_SAFE_AREA);
    const tokens = useTokens();
    const setSafeAreaCallback = useCallback((safeArea)=>{
        const getValue = (value)=>{
            let computedValue = value;
            if (typeof value === 'string' && value.startsWith('spacing.') && value in tokens) {
                computedValue = tokens[value];
            }
            return typeof computedValue === 'string' ? computedValue : `${computedValue}px`;
        };
        // Get value from tokens if needed (basic or responsive)
        const computeSafeArea = (value)=>{
            if (typeof value === 'object') {
                const result = {};
                for (const [key, responsiveValue] of Object.entries(value)){
                    result[key] = getValue(responsiveValue);
                }
                return result;
            }
            return getValue(value);
        };
        const newSafeArea = {};
        for (const [key, sideValue] of Object.entries(safeArea)){
            newSafeArea[key] = computeSafeArea(sideValue);
        }
        setSafeArea((prevState)=>({
                ...prevState,
                ...newSafeArea,
                ...Object.entries(safeArea).reduce((acc, [key, value])=>({
                        ...acc,
                        [key]: computeSafeArea(value)
                    }), {})
            }));
    }, [
        setSafeArea,
        tokens
    ]);
    const resetSafeArea = useCallback(()=>setSafeArea(DEFAULT_SAFE_AREA), [
        setSafeArea
    ]);
    return /*#__PURE__*/ jsx(SafeAreaContext.Provider, {
        value: {
            safeArea,
            setSafeArea: setSafeAreaCallback,
            resetSafeArea
        },
        children: children
    });
};
const useSafeArea = ()=>{
    return useContext(SafeAreaContext);
};

const useInsertionEffectWithLayoutFallback = useInsertionEffect || useLayoutEffect;
// maintain place over rerenders.
// initial render from browser, insertBefore context.sheet.tags[0] or if a style hasn't been inserted there yet, appendChild
// initial client-side render from SSR, use place of hydrating tag
const GlobalStyle = /*#__PURE__*/ memo(({ styles })=>{
    const isServer = useIsServer();
    const serialized = serializeStyles([
        styles
    ], undefined);
    if (!isServer) {
        return /*#__PURE__*/ jsx(BrowserGlobalStyle, {
            serialized: serialized
        });
    }
    let serializedNames = serialized.name;
    let serializedStyles = serialized.styles;
    let next = serialized.next;
    while(next !== undefined){
        serializedNames += ' ' + next.name;
        serializedStyles += next.styles;
        next = next.next;
    }
    const shouldCache = cache.compat === true;
    const rules = cache.insert(``, {
        name: serializedNames,
        styles: serializedStyles
    }, cache.sheet, shouldCache);
    if (shouldCache) {
        return null;
    }
    return(// eslint-disable-next-line no-restricted-syntax
    /*#__PURE__*/ jsx("style", {
        [`data-emotion`]: `${cache.key}-global ${serializedNames}`,
        dangerouslySetInnerHTML: {
            __html: rules
        },
        nonce: cache.sheet.nonce
    }));
});
const BrowserGlobalStyle = ({ serialized })=>{
    const sheetRef = useRef(undefined);
    useInsertionEffectWithLayoutFallback(()=>{
        const key = `${cache.key}-global`;
        const sheet = new StyleSheet({
            key,
            container: cache.sheet.container,
            nonce: cache.sheet.nonce
        });
        let rehydrating = false;
        const node = document.querySelector(`style[data-emotion="${key} ${serialized.name}"]`);
        if (cache.sheet.tags[0]) {
            sheet.before = cache.sheet.tags[0];
        }
        if (node !== null) {
            rehydrating = true;
            // clear the hash so this node won't be recognizable as rehydratable by other <Global/>s
            node.setAttribute('data-emotion', key);
            sheet.hydrate([
                node
            ]);
        }
        sheetRef.current = [
            sheet,
            rehydrating
        ];
        return ()=>{
            sheet.flush();
        };
    }, [
        cache
    ]);
    useInsertionEffectWithLayoutFallback(()=>{
        const sheetRefCurrent = sheetRef.current;
        if (!sheetRefCurrent) return;
        const [sheet, rehydrating] = sheetRefCurrent;
        if (rehydrating) {
            sheetRefCurrent[1] = false;
            return;
        }
        if (serialized.next !== undefined) {
            // insert keyframes
            insertStyles(cache, serialized.next, true);
        }
        if (sheet.tags.length > 0) {
            // if this doesn't exist then it will be null so the style element will be appended
            const element = sheet.tags[sheet.tags.length - 1]?.nextElementSibling || null;
            sheet.before = element;
            sheet.flush();
        }
        cache.insert(``, serialized, sheet, false);
    }, [
        cache,
        serialized.name
    ]);
    return null;
};

/**
 * Join the main scoping selector and the portal selector with the css selector
 * @param scopingSelectors Tuple of one or two scoping selectors
 * @param cssSelector Optional css selector (can be multiple with array)
 * @param fallbackSelectorIfEmpty Fallback when there is no scoping selector
 * @example
 * // returns '.mfe :where(h1), :where(.gemini_portal) :where(h1)'
 * joinScopingSelectorsWithCssSelector(['.mfe', ':where(.gemini_portal)'], ':where(h1)')
 * @example
 * // returns '.mfe, :where(.gemini_portal)'
 * joinScopingSelectorsWithCssSelector(['.mfe', ':where(.gemini_portal)'])
 * @example
 * // returns ':where(h1), :where(.gemini_portal) :where(h1)'
 * joinScopingSelectorsWithCssSelector(['', ':where(.gemini_portal)'], ':where(h1)')
 * @returns The joined scoping selectors with the css selector
 */ const joinScopingSelectorsWithCssSelector = (scopingSelectors, cssSelector, fallbackSelectorIfEmpty = '')=>scopingSelectors.map((scopingSelector)=>{
        const selectors = Array.isArray(cssSelector) ? cssSelector : [
            cssSelector
        ];
        return selectors.map((selector)=>`${scopingSelector || fallbackSelectorIfEmpty} ${selector}`.trim()).join(', ');
    }).filter(Boolean).join(', ');
/**
 * Taken from Sanitize.css and adapted to add scoped version
 *
 */ const resetWithSanitizeRules = ({ scopingSelectors })=>{
    /**
	 * @see: https://github.com/csstools/sanitize.css/blob/main/assets.css
	 */ const assets = {
        /**
		 * Restrict sizing to the page width in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(iframe, img, input, video, select, textarea)')]: {
            maxWidth: '100%'
        },
        [joinScopingSelectorsWithCssSelector(scopingSelectors, `:where(img, input, video, select, textarea)`)]: {
            height: 'auto'
        }
    };
    /**
	 * @see: https://github.com/csstools/sanitize.css/blob/main/forms.css
	 */ const forms = {
        /**
		 * 1. Change the inconsistent appearance in all browsers (opinionated).
		 * 2. Add typography inheritance in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(button, input, select, textarea)')]: {
            padding: '0.25em 0.375em' /* 1 */ ,
            color: 'inherit' /* 1 */ ,
            font: 'inherit' /* 2 */ ,
            letterSpacing: 'inherit' /* 2 */ ,
            backgroundColor: 'transparent' /* 1 */ ,
            border: '1px solid WindowFrame' /* 1 */ 
        },
        /**
		 * Change the inconsistent appearance in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(select)')]: {
            paddingRight: '1em',
            background: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='4'%3E%3Cpath d='M4 0h6L7 4'/%3E%3C/svg%3E\") no-repeat right center / 1em",
            borderRadius: 0,
            WebkitAppearance: 'none',
            appearance: 'none'
        },
        /**
		 * Don't show the arrow for multiple choice selects
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(select[multiple])')]: {
            backgroundImage: 'none'
        },
        /**
		 * Remove the border and padding in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ":where([type='color' i], [type='range' i])")]: {
            padding: 0,
            borderWidth: 0
        }
    };
    /**
	 * @see https://github.com/csstools/sanitize.css/blob/main/sanitize.css
	 */ const base = {
        /* Document
		 * ========================================================================== */ /**
		 * 1. Add border box sizing in all browsers (opinionated).
		 * 2. Backgrounds do not repeat by default (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, [
            '*',
            '::before',
            '::after'
        ])]: {
            boxSizing: 'border-box' /* 1 */ ,
            backgroundRepeat: 'no-repeat' /* 2 */ 
        },
        /**
		 * 1. Add text decoration inheritance in all browsers (opinionated).
		 * 2. Add vertical alignment inheritance in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, [
            '::before',
            '::after'
        ])]: {
            textDecoration: 'inherit' /* 1 */ ,
            verticalAlign: 'inherit' /* 2 */ 
        },
        /**
		 * 1. Change the line height in all browsers (opinionated).
		 * 2. Breaks words to prevent overflow in all browsers (opinionated).
		 * 3. Use a 4-space tab width in all browsers (opinionated).
		 * 4. Remove the grey highlight on links in iOS (opinionated).
		 * 5. Prevent adjustments of font size after orientation changes in iOS.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, '', ':where(:root)')]: {
            lineHeight: '1.5' /* 1 */ ,
            overflowWrap: 'break-word' /* 2 */ ,
            MozTabSize: '4' /* 3 */ ,
            tabSize: '4' /* 3 */ ,
            WebkitTapHighlightColor: 'transparent' /* 4 */ ,
            textSizeAdjust: '100%' /* 5 */ 
        },
        /* Sections
		 * ========================================================================== */ /**
		 * Remove the margin in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, '', ':where(body)')]: {
            margin: 0
        },
        /**
		 * Correct the font size and margin on 'h1' elements within 'section' and
		 * 'article' contexts in Chrome, Edge, Firefox, and Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(h1)')]: {
            margin: '0.67em 0',
            fontSize: '2em'
        },
        /* Grouping content
		 * ========================================================================== */ /**
		 * Remove the margin on nested lists in Chrome, Edge, and Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(dl, ol, ul) :where(dl, ol, ul)')]: {
            margin: 0
        },
        /**
		 * 1. Correct the inheritance of border color in Firefox.
		 * 2. Add the correct box sizing in Firefox.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(hr)')]: {
            height: 0 /* 2 */ ,
            color: 'inherit' /* 1 */ 
        },
        /**
		 * Remove the list style on navigation lists in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(nav) :where(ol, ul)')]: {
            padding: 0,
            listStyleType: 'none'
        },
        /**
		 * 1. Correct the inheritance and scaling of font size in all browsers.
		 * 2. Correct the odd 'em' font sizing in all browsers.
		 * 3. Prevent overflow of the container in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(pre)')]: {
            overflow: 'auto' /* 3 */ ,
            fontSize: '1em' /* 2 */ ,
            fontFamily: 'monospace, monospace' /* 1 */ 
        },
        /* Text-level semantics
		 * ========================================================================== */ /**
		 * Add the correct text decoration in Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(abbr[title])')]: {
            textDecoration: [
                'underline',
                'underline dotted'
            ]
        },
        /**
		 * Add the correct font weight in Chrome, Edge, and Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(b, strong)')]: {
            fontWeight: 'bolder'
        },
        /**
		 * 1. Correct the inheritance and scaling of font size in all browsers.
		 * 2. Correct the odd 'em' font sizing in all browsers.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(code, kbd, samp)')]: {
            fontSize: '1em' /* 2 */ ,
            fontFamily: 'monospace, monospace' /* 1 */ 
        },
        /**
		 * Add the correct font size in all browsers.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(small)')]: {
            fontSize: '80%'
        },
        /* Embedded content
		 * ========================================================================== */ /*
		 * Change the alignment on media elements in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(audio, canvas, iframe, img, svg, video)')]: {
            verticalAlign: 'middle'
        },
        /**
		 * Remove the border on iframes in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(iframe)')]: {
            borderStyle: 'none'
        },
        /**
		 * Change the fill color to match the text color in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(svg:not([fill]))')]: {
            fill: 'currentcolor'
        },
        /* Tabular data
		 * ========================================================================== */ /**
		 * 1. Collapse border spacing in all browsers (opinionated).
		 * 2. Correct table border color in Chrome, Edge, and Safari.
		 * 3. Remove text indentation from table contents in Chrome, Edge, and Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(table)')]: {
            textIndent: 0 /* 3 */ ,
            borderColor: 'currentcolor' /* 2 */ ,
            borderCollapse: 'collapse' /* 1 */ 
        },
        /* Forms
		 * ========================================================================== */ /**
		 * Remove the margin on controls in Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(button, input, select)')]: {
            margin: 0
        },
        /**
		 * Correct the inability to style buttons in iOS and Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ":where(button, [type='button' i], [type='reset' i], [type='submit' i])")]: {
            WebkitAppearance: 'button'
        },
        /**
		 * Change the inconsistent appearance in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(fieldset)')]: {
            border: '1px solid #a0a0a0'
        },
        /**
		 * Add the correct vertical alignment in Chrome, Edge, and Firefox.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(progress)')]: {
            verticalAlign: 'baseline'
        },
        /**
		 * 1. Remove the margin in Firefox and Safari.
		 * 3. Change the resize direction in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(textarea)')]: {
            margin: 0 /* 1 */ ,
            resize: 'vertical' /* 3 */ 
        },
        /**
		 * 1. Correct the odd appearance in Chrome, Edge, and Safari.
		 * 2. Correct the outline style in Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ":where([type='search' i])")]: {
            outlineOffset: '-2px' /* 2 */ ,
            WebkitAppearance: 'textfield' /* 1 */ 
        },
        /**
		 * Correct the cursor style of increment and decrement buttons in Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, [
            '::-webkit-inner-spin-button',
            '::-webkit-outer-spin-button'
        ])]: {
            height: 'auto'
        },
        /**
		 * Correct the text style of placeholders in Chrome, Edge, and Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, '::-webkit-input-placeholder')]: {
            color: 'inherit',
            opacity: 0.54
        },
        /**
		 * Remove the inner padding in Chrome, Edge, and Safari on macOS.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, '::-webkit-search-decoration')]: {
            WebkitAppearance: 'none'
        },
        /**
		 * 1. Correct the inability to style upload buttons in iOS and Safari.
		 * 2. Change font properties to 'inherit' in Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, '::-webkit-file-upload-button')]: {
            font: 'inherit' /* 2 */ ,
            WebkitAppearance: 'button' /* 1 */ 
        },
        /* Interactive
		 * ========================================================================== */ /*
		 * Add the correct styles in Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(dialog)')]: {
            position: 'absolute',
            right: 0,
            left: 0,
            width: 'fit-content',
            height: 'fit-content',
            margin: 'auto',
            padding: '1em',
            color: 'black',
            backgroundColor: 'white',
            border: 'solid'
        },
        [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(dialog:not([open]))')]: {
            display: 'none'
        },
        /*
		 * Add the correct display in Safari.
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ':where(details > summary:first-of-type)')]: {
            display: 'list-item'
        },
        /* Accessibility
		 * ========================================================================== */ /**
		 * Change the cursor on busy elements in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ":where([aria-busy='true' i])")]: {
            cursor: 'progress'
        },
        /*
		 * Change the cursor on disabled, not-editable, or otherwise
		 * inoperable elements in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ":where([aria-disabled='true' i], [disabled])")]: {
            cursor: 'not-allowed'
        },
        /*
		 * Change the display on visually hidden accessible elements
		 * in all browsers (opinionated).
		 */ [joinScopingSelectorsWithCssSelector(scopingSelectors, ":where([aria-hidden='false' i][hidden])")]: {
            display: 'initial'
        },
        [joinScopingSelectorsWithCssSelector(scopingSelectors, ":where([aria-hidden='false' i][hidden]:not(:focus))")]: {
            position: 'absolute',
            clip: 'rect(0, 0, 0, 0)'
        }
    };
    return {
        ...base,
        ...assets,
        ...forms
    };
};

/**
 * Apply default typography values
 */ const resetWithThemeableRules = ({ tokens, scopingSelectors })=>{
    return {
        // Should only concern main scoping selector to avoid background color on Gemini component that doesn't require it
        ...scopingSelectors.length === 2 ? {
            [`${scopingSelectors[0].trim()}`]: {
                backgroundColor: tokens['color.background.default']
            },
            [`${scopingSelectors[0].trim()}, ${scopingSelectors[1].trim()}`]: {
                ...tokens['settings'].font.default,
                color: tokens['color.content.default.default']
            }
        } : {
            [`${scopingSelectors[0].trim()}`]: {
                ...tokens['settings'].font.default,
                backgroundColor: tokens['color.background.default'],
                color: tokens['color.content.default.default']
            }
        }
    };
};

const UNSAFE_GEMINI_PORTAL_CLASS_SELECTOR = 'unsafe_gemini_portal';
/**
 * Styling reset rules that should apply as a side effect on a scoped application part
 */ const StylingReset = ({ scopingSelector })=>{
    const tokens = useTokens();
    const selector = scopingSelector ? `:where(${scopingSelector})` : undefined;
    const withSanitizeRules = useMemo(()=>resetWithSanitizeRules({
            scopingSelectors: selector ? [
                selector,
                `:where(.${UNSAFE_GEMINI_PORTAL_CLASS_SELECTOR})`
            ] : [
                ''
            ]
        }), [
        selector
    ]);
    const withThemeableRules = useMemo(()=>resetWithThemeableRules({
            tokens,
            scopingSelectors: selector ? [
                selector,
                `:where(.${UNSAFE_GEMINI_PORTAL_CLASS_SELECTOR})`
            ] : [
                ':where(body)'
            ]
        }), [
        selector,
        tokens
    ]);
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(GlobalStyle, {
                styles: withSanitizeRules
            }),
            /*#__PURE__*/ jsx(GlobalStyle, {
                styles: withThemeableRules
            })
        ]
    });
};

const MAX_VISIBLE_NOTIFICATION = 3;
const useNotificationState = ()=>{
    const notificationQueue = useMemo(()=>new ToastQueue(), []);
    const subscribe = useCallback((fn)=>notificationQueue.subscribe(fn), [
        notificationQueue
    ]);
    const getSnapshot = useCallback(()=>notificationQueue.visibleToasts, [
        notificationQueue
    ]);
    const visibleToasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const add = useCallback((content, options)=>notificationQueue.add(content, options?.onClose ? {
            onClose: options.onClose
        } : undefined), [
        notificationQueue
    ]);
    const close = useCallback((key)=>{
        notificationQueue.close(key);
    }, [
        notificationQueue
    ]);
    const pauseAll = useCallback(()=>{
        notificationQueue.pauseAll();
    }, [
        notificationQueue
    ]);
    const resumeAll = useCallback(()=>{
        notificationQueue.resumeAll();
    }, [
        notificationQueue
    ]);
    return {
        visibleToasts,
        add,
        close,
        pauseAll,
        resumeAll
    };
};

const NotificationContext = /*#__PURE__*/ createContext(null);
const NotificationProvider = ({ children })=>{
    const state = useNotificationState();
    const contextValue = useMemo(()=>{
        return {
            add: state.add,
            close: state.close
        };
    }, [
        state.add,
        state.close
    ]);
    return /*#__PURE__*/ jsxs(NotificationContext.Provider, {
        value: contextValue,
        children: [
            children,
            state.visibleToasts.length > 0 ? /*#__PURE__*/ createPortal(/*#__PURE__*/ jsx(NotificationRegion, {
                state: state,
                children: state.visibleToasts.reverse().slice(0, MAX_VISIBLE_NOTIFICATION).map((notification)=>/*#__PURE__*/ jsx(NotificationComponent, {
                        toast: notification,
                        state: state
                    }, notification.key))
            }), document.body) : null
        ]
    });
};
const NotificationComponent = ({ toast, state })=>{
    const { component: Notification, ...restProps } = toast.content;
    return /*#__PURE__*/ jsx(Notification, {
        state: state,
        toast: {
            ...toast,
            content: restProps
        }
    });
};
const NotificationRegion = ({ children, state })=>{
    const ref = useRef(null);
    const tokens = useTokens();
    const { regionProps } = useToastRegion({}, state, ref);
    const { safeArea: { bottom: bottomOffset } } = useSafeArea();
    const marginBottom = useMemo(()=>mapMarginBottom({
            offset: bottomOffset,
            tokens
        }), [
        bottomOffset,
        tokens
    ]);
    return /*#__PURE__*/ jsx(Box, {
        ...regionProps,
        unsafe_className: UNSAFE_GEMINI_PORTAL_CLASS_SELECTOR,
        ref: ref,
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        marginBottom: marginBottom,
        outlineStyle: "none",
        alignItems: "center",
        pointerEvents: "none",
        zIndex: "zIndex.4",
        children: children
    });
};
const createMarginValue = (defaultOffset, isDefault)=>{
    return (offset)=>{
        return !isDefault && !offset ? undefined : `max(${defaultOffset}${offset ? ` + ${offset}` : ''}, 0px)`;
    };
};
const marginBottomResponsive = (tokens)=>{
    const gapMargin = tokens['unsafe_component.snackbar.spacing.margin.gap'];
    const baseBottomMargin = `${tokens['unsafe_component.snackbar.spacing.margin.container.bottom.base']} - ${gapMargin}`;
    const smBottomMargin = `${tokens['unsafe_component.snackbar.spacing.margin.container.bottom.sm']} - ${gapMargin}`;
    const lgBottomMargin = `${tokens['unsafe_component.snackbar.spacing.margin.container.bottom.lg']} - ${gapMargin}`;
    return {
        /**
		 * breakpoint.base, breakpoint.sm, breakpoint.lg are the 3 default values
		 */ 'breakpoint.base': createMarginValue(baseBottomMargin, true),
        'breakpoint.sm': createMarginValue(smBottomMargin, true),
        'breakpoint.lg': createMarginValue(lgBottomMargin, true),
        /**
		 * Rest of breakpoints, useful if user set a breakpoint different than the 3 defaults
		 */ 'breakpoint.xs': createMarginValue(baseBottomMargin),
        'breakpoint.md': createMarginValue(smBottomMargin),
        'breakpoint.xl': createMarginValue(lgBottomMargin),
        'breakpoint.xxl': createMarginValue(lgBottomMargin),
        'breakpoint.xxxl': createMarginValue(lgBottomMargin)
    };
};
const mapMarginBottom = ({ offset, tokens })=>{
    const marginBottomResponsiveValues = marginBottomResponsive(tokens);
    const result = {};
    const isObjectOffset = offset && typeof offset === 'object';
    for (const [key, marginValue] of Object.entries(marginBottomResponsiveValues)){
        const computedValue = marginValue(isObjectOffset ? offset[key] : offset);
        if (computedValue) {
            result[key] = computedValue;
        }
    }
    return result;
};

const FONT_WEIGHTS = [
    'regular',
    'bold'
];
const fontAssetsBaseUrl = '/assets/gemini/fonts/v1/';
// Fallback URL for font assets, used when the token fontFamily is "Inter", which is referenced to "AVIV" brand
// Will be removed, after the routing for AVIV will be implemented
const fontAssetsBaseUrlFallback = 'https://assets.meilleursagents.com/web/gemini/fonts/20250120/';
function toKebabCase(str) {
    return str.trim().replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\W/g, (m)=>/[À-ž]/.test(m) ? m : '-').replace(/^-+|-+$/g, '').toLowerCase();
}
const Metadata = ()=>{
    const tokens = useTokens();
    const serverContext = useUnsafeServerContext();
    const assetsBaseUrl = useAssetsBaseUrl();
    const baseUrl = assetsBaseUrl.replace(/\/$/, '');
    let combinedUrl = `${baseUrl}${fontAssetsBaseUrl}`;
    if (tokens.settings.font.default.fontFamily?.toLowerCase().startsWith('"inter"')) {
        combinedUrl = fontAssetsBaseUrlFallback;
    }
    const links = useMemo(()=>{
        const fonts = [];
        const css = tokens.settings.font.list.map((font)=>{
            const fontName = toKebabCase(font);
            FONT_WEIGHTS.forEach((type)=>{
                fonts.push({
                    id: `metadata-${fontName}-${type}`,
                    href: `${combinedUrl}${fontName}/${fontName}-${type}.woff2`,
                    rel: 'preload',
                    as: 'font',
                    type: 'font/woff2',
                    // Prevents loading fonts twice
                    crossOrigin: ''
                });
            });
            return {
                id: `metadata-${fontName}`,
                href: `${combinedUrl}${fontName}/font-face.css`,
                rel: 'stylesheet',
                type: 'text/css'
            };
        });
        return [
            ...css,
            ...fonts
        ];
    }, [
        tokens,
        combinedUrl
    ]);
    if (serverContext) {
        links.map(({ id, ...restProps })=>{
            // eslint-disable-next-line no-restricted-syntax
            serverContext.collectCriticalElement(id, /*#__PURE__*/ jsx("link", {
                ...restProps
            }));
        });
    }
    useLayoutEffect(()=>{
        links.forEach(({ id, ...restProps })=>{
            const isAlreadyInDOM = Boolean(document.querySelector(`[data-gemini="${id}"]`));
            if (isAlreadyInDOM) {
                return;
            }
            const linkElement = document.createElement('link');
            Object.keys(restProps).forEach((key)=>{
                linkElement.setAttribute(key, restProps[key]);
            });
            linkElement.setAttribute('data-gemini', id);
            document.head.appendChild(linkElement);
        });
    }, [
        links
    ]);
    return null;
};

const SideEffectProvider = ({ scopingSelector })=>{
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(StylingReset, {
                scopingSelector: scopingSelector
            }),
            /*#__PURE__*/ jsx(Metadata, {})
        ]
    });
};

const GeminiProvider = /*#__PURE__*/ memo(({ children, colorScheme = 'light', locale = DEFAULT_LOCALE, renderLink, scopingSelector, theme, assetsBaseUrl })=>/*#__PURE__*/ jsx(ServerProvider, {
        children: /*#__PURE__*/ jsx(TokensProvider, {
            theme: theme,
            colorScheme: colorScheme,
            children: /*#__PURE__*/ jsx(AssetsProvider, {
                assetsBaseUrl: assetsBaseUrl,
                children: /*#__PURE__*/ jsx(SafeAreaProvider, {
                    children: /*#__PURE__*/ jsx(OverlayProvider, {
                        scopingSelector: scopingSelector,
                        children: /*#__PURE__*/ jsx(NotificationProvider, {
                            children: /*#__PURE__*/ jsxs(BreakpointProvider, {
                                children: [
                                    /*#__PURE__*/ jsx(SideEffectProvider, {
                                        scopingSelector: scopingSelector
                                    }),
                                    /*#__PURE__*/ jsx(LocaleProvider, {
                                        locale: locale,
                                        children: /*#__PURE__*/ jsx(RenderLinkProvider, {
                                            renderLink: renderLink,
                                            children: children
                                        })
                                    })
                                ]
                            })
                        })
                    })
                })
            })
        })
    }));

const mergeProps = (...args)=>{
    return mergeProps$1(...args);
};

/**
 * Offers an object ref for a given callback ref or an object ref. Especially
 * helfpul when passing forwarded refs (created using `React.forwardRef`) to
 * Gemini hooks.
 *
 * @param forwardedRef The original ref intended to be used.
 * @returns An object ref that updates the given ref.
 * @see https://reactjs.org/docs/forwarding-refs.html
 */ function useObjectRef(forwardedRef) {
    return useObjectRef$1(forwardedRef);
}

const FocusableProvider = /*#__PURE__*/ forwardRef(({ children, ...props }, ref)=>{
    return /*#__PURE__*/ jsx(FocusableProvider$1, {
        ref: ref,
        ...props,
        children: children
    });
});
const useFocusable = (ref, { isDisabled, excludeFromTabOrder, within, unsafe_ignoreProviderProps, onFocus, onFocusChange, onBlur, onKeyDown, onKeyUp } = {})=>{
    const { focusableProps } = useFocusable$1({
        excludeFromTabOrder: Boolean(excludeFromTabOrder),
        isDisabled: Boolean(isDisabled) || Boolean(within) || Boolean(unsafe_ignoreProviderProps),
        ...onFocusChange ? {
            onFocusChange
        } : {},
        ...onFocus ? {
            onFocus
        } : {},
        ...onBlur ? {
            onBlur
        } : {},
        ...onKeyDown ? {
            onKeyDown
        } : {},
        ...onKeyUp ? {
            onKeyUp
        } : {}
    }, ref);
    const { focusProps, isFocused, isFocusVisible } = useFocusRing({
        isTextInput: ref.current?.tagName === 'INPUT' || false,
        within: Boolean(within)
    });
    return {
        boxProps: mergeProps(focusableProps, focusProps),
        isFocused,
        isFocusVisible
    };
};

const useHoverable = ({ onHoverStart, onHoverEnd, onHoverChange, ...props } = {})=>{
    const { hoverProps, isHovered } = useHover({
        ...props,
        ...onHoverStart ? {
            onHoverStart
        } : {},
        ...onHoverEnd ? {
            onHoverEnd
        } : {},
        ...onHoverChange ? {
            onHoverChange
        } : {}
    });
    return {
        boxProps: hoverProps,
        isHovered
    };
};

const useResizeObserver = (ref, props)=>{
    return useResizeObserver$1({
        ref,
        ...props
    });
};

/**
 * Custom hook to map `Tooltip` props to `Box` ones with all needed interactive and accessible HTML attributes.
 * @param ref Ref collectionto target the trigger and popover element
 * @param props Property input
 * @returns Box props and states
 */ const usePopover = (ref, props)=>{
    const { placement = 'bottom', alignment = 'middle', offset = 0, close, isOpen, unsafe_isNonModal = false, shouldFlip = true, unsafe_shouldUpdatePosition = true } = props;
    const tokens = useTokens();
    const zIndex = useZIndexContext();
    const isHorizontal = placement === 'left' || placement === 'right';
    let computedPlacement;
    if (isHorizontal && alignment === 'start') computedPlacement = `${placement} top`;
    else if (isHorizontal && alignment === 'end') computedPlacement = `${placement} bottom`;
    else if (!isHorizontal && alignment !== 'middle') computedPlacement = `${placement} ${alignment}`;
    else computedPlacement = `${placement}`;
    const { popoverProps: { style: popoverStyleProps, ...restPopoverProps }, arrowProps: { style: arrowStyleProps, ...restArrowProps }, underlayProps, placement: placementReactAria } = usePopover$1({
        triggerRef: ref.trigger,
        popoverRef: ref.popover,
        placement: computedPlacement,
        offset,
        shouldFlip,
        isNonModal: unsafe_isNonModal,
        shouldUpdatePosition: unsafe_shouldUpdatePosition
    }, {
        close,
        isOpen
    });
    return {
        boxProps: {
            popover: {
                unsafe_style: {
                    ...popoverStyleProps,
                    // Enforce our zIndex value
                    zIndex: zIndex || tokens['zIndex.1']
                },
                ...restPopoverProps
            },
            arrow: {
                unsafe_style: arrowStyleProps,
                ...restArrowProps
            },
            underlay: {
                ...underlayProps,
                unsafe_style: {
                    // Enforce our zIndex value to be the same as our Popover, underlay should always be before the popover
                    zIndex: zIndex || tokens['zIndex.1']
                }
            }
        },
        placement: placementReactAria
    };
};

const getOuputEvent = (event)=>({
        pointerType: event.pointerType,
        element: event.target,
        eventType: event.type,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        altKey: event.altKey,
        x: event.x,
        y: event.y,
        continuePropagation: event.continuePropagation
    });
const usePressable = (props)=>{
    const { onPress, onPressStart, onPressEnd, isDisabled, preventFocusOnPress, ...rest } = props || {};
    const { pressProps, isPressed } = usePress({
        ...rest,
        preventFocusOnPress: Boolean(preventFocusOnPress),
        isDisabled: Boolean(isDisabled),
        onPress: (event)=>{
            if (onPress) {
                onPress(getOuputEvent(event));
            }
        },
        onPressStart: (event)=>{
            if (onPressStart) {
                onPressStart(getOuputEvent(event));
            }
        },
        onPressEnd: (event)=>{
            if (onPressEnd) {
                onPressEnd(getOuputEvent(event));
            }
        }
    });
    return {
        boxProps: pressProps,
        isPressed
    };
};

const visualViewport = typeof document !== 'undefined' && window.visualViewport;
// HTML input types that do not cause the software keyboard to appear.
const nonTextInputTypes = new Set([
    'checkbox',
    'radio',
    'range',
    'color',
    'file',
    'image',
    'button',
    'submit',
    'reset'
]);
// The number of active usePreventScroll calls. Used to determine whether to revert back to the original page style/scroll position
let preventScrollCount = 0;
let restore;
// Added by Gemini
let previousFocusForScroll = null;
/**
 * Prevents scrolling on the document body on mount, and
 * restores it on unmount. Also ensures that content does not
 * shift due to the scrollbars disappearing.
 */ function usePreventScroll(options = {}) {
    const { isDisabled } = options;
    useLayoutEffect$2(()=>{
        if (isDisabled) {
            return;
        }
        preventScrollCount++;
        if (preventScrollCount === 1) {
            if (isIOS()) {
                restore = preventScrollMobileSafari();
            } else {
                restore = preventScrollStandard();
            }
        }
        return ()=>{
            preventScrollCount--;
            if (preventScrollCount === 0) {
                restore();
            }
        };
    }, [
        isDisabled
    ]);
}
// For most browsers, all we need to do is set `overflow: hidden` on the root element, and
// add some padding to prevent the page from shifting when the scrollbar is hidden.
function preventScrollStandard() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    return chain(scrollbarWidth > 0 && // Use scrollbar-gutter when supported because it also works for fixed positioned elements.
    ('scrollbarGutter' in document.documentElement.style ? setStyle(document.documentElement, 'scrollbarGutter', 'stable') : setStyle(document.documentElement, 'paddingRight', `${scrollbarWidth}px`)), setStyle(document.documentElement, 'overflow', 'hidden'));
}
// Mobile Safari is a whole different beast. Even with overflow: hidden,
// it still scrolls the page in many situations:
//
// 1. When the bottom toolbar and address bar are collapsed, page scrolling is always allowed.
// 2. When the keyboard is visible, the viewport does not resize. Instead, the keyboard covers part of
//    it, so it becomes scrollable.
// 3. When tapping on an input, the page always scrolls so that the input is centered in the visual viewport.
//    This may cause even fixed position elements to scroll off the screen.
// 4. When using the next/previous buttons in the keyboard to navigate between inputs, the whole page always
//    scrolls, even if the input is inside a nested scrollable element that could be scrolled instead.
//
// In order to work around these cases, and prevent scrolling without jankiness, we do a few things:
//
// 1. Prevent default on `touchmove` events that are not in a scrollable element. This prevents touch scrolling
//    on the window.
// 2. Set `overscroll-behavior: contain` on nested scrollable regions so they do not scroll the page when at
//    the top or bottom. Work around a bug where this does not work when the element does not actually overflow
//    by preventing default in a `touchmove` event.
// 3. Prevent default on `touchend` events on input elements and handle focusing the element ourselves.
// 4. When focusing an input, apply a transform to trick Safari into thinking the input is at the top
//    of the page, which prevents it from scrolling the page. After the input is focused, scroll the element
//    into view ourselves, without scrolling the whole page.
// 5. Offset the body by the scroll position using a negative margin and scroll to the top. This should appear the
//    same visually, but makes the actual scroll position always zero. This is required to make all of the
//    above work or Safari will still try to scroll the page when focusing an input.
// 6. As a last resort, handle window scroll events, and scroll back to the top. This can happen when attempting
//    to navigate to an input with the next/previous buttons that's outside a modal.
function preventScrollMobileSafari() {
    let scrollable;
    let restoreScrollableStyles;
    const onTouchStart = (e)=>{
        // Store the nearest scrollable parent element from the element that the user touched.
        scrollable = getScrollParent(e.target, true);
        if (scrollable === document.documentElement && scrollable === document.body) {
            return;
        }
        // Prevent scrolling up when at the top and scrolling down when at the bottom
        // of a nested scrollable area, otherwise mobile Safari will start scrolling
        // the window instead.
        if (scrollable instanceof HTMLElement && window.getComputedStyle(scrollable).overscrollBehavior === 'auto') {
            restoreScrollableStyles = setStyle(scrollable, 'overscrollBehavior', 'contain');
        }
    };
    const onTouchMove = (e)=>{
        // Prevent scrolling the window.
        if (!scrollable || scrollable === document.documentElement || scrollable === document.body) {
            e.preventDefault();
            return;
        }
        // overscroll-behavior should prevent scroll chaining, but currently does not
        // if the element doesn't actually overflow. https://bugs.webkit.org/show_bug.cgi?id=243452
        // This checks that both the width and height do not overflow, otherwise we might
        // block horizontal scrolling too. In that case, adding `touch-action: pan-x` to
        // the element will prevent vertical page scrolling. We can't add that automatically
        // because it must be set before the touchstart event.
        if (scrollable.scrollHeight === scrollable.clientHeight && scrollable.scrollWidth === scrollable.clientWidth) {
            e.preventDefault();
        }
    };
    const onTouchEnd = ()=>{
        if (restoreScrollableStyles) {
            restoreScrollableStyles();
        }
    };
    const onFocus = (e)=>{
        const target = e.target;
        // Modified by Gemini
        if (previousFocusForScroll !== e.target) previousFocusForScroll = null;
        // Modified by Gemini
        if (willOpenKeyboard(target) && previousFocusForScroll !== e.target) {
            setupStyles();
            // Apply a transform to trick Safari into thinking the input is at the top of the page
            // so it doesn't try to scroll it into view.
            target.style.transform = 'translateY(-2000px)';
            requestAnimationFrame(()=>{
                target.style.transform = '';
                // This will have prevented the browser from scrolling the focused element into view,
                // so we need to do this ourselves in a way that doesn't cause the whole page to scroll.
                if (visualViewport) {
                    if (visualViewport.height < window.innerHeight) {
                        // If the keyboard is already visible, do this after one additional frame
                        // to wait for the transform to be removed.
                        requestAnimationFrame(()=>{
                            // Modified by Gemini
                            if (previousFocusForScroll === e.target) return;
                            // Modified by Gemini
                            target.scrollIntoView({
                                block: 'center',
                                behavior: 'smooth'
                            });
                            previousFocusForScroll = e.target;
                        });
                    } else {
                        // Otherwise, wait for the visual viewport to resize before scrolling so we can
                        // measure the correct position to scroll to.
                        visualViewport.addEventListener('resize', ()=>{
                            // Modified by Gemini
                            if (previousFocusForScroll === e.target) return;
                            // Modified by Gemini
                            target.scrollIntoView({
                                block: 'center',
                                behavior: 'smooth'
                            });
                            previousFocusForScroll = e.target;
                        }, {
                            once: true
                        });
                    }
                }
            });
        }
    };
    let restoreStyles = null;
    const setupStyles = ()=>{
        if (restoreStyles) {
            return;
        }
        const onWindowScroll = ()=>{
            // Last resort. If the window scrolled, scroll it back to the top.
            // It should always be at the top because the body will have a negative margin (see below).
            window.scrollTo(0, 0);
        };
        // Record the original scroll position so we can restore it.
        // Then apply a negative margin to the body to offset it by the scroll position. This will
        // enable us to scroll the window to the top, which is required for the rest of this to work.
        const scrollX = window.pageXOffset;
        const scrollY = window.pageYOffset;
        restoreStyles = chain(addEvent(window, 'scroll', onWindowScroll), setStyle(document.documentElement, 'paddingRight', `${window.innerWidth - document.documentElement.clientWidth}px`), setStyle(document.documentElement, 'overflow', 'hidden'), setStyle(document.body, 'marginTop', `-${scrollY}px`), ()=>{
            window.scrollTo(scrollX, scrollY);
        });
        // Scroll to the top. The negative margin on the body will make this appear the same.
        window.scrollTo(0, 0);
    };
    const removeEvents = chain(addEvent(document, 'touchstart', onTouchStart, {
        passive: false,
        capture: true
    }), addEvent(document, 'touchmove', onTouchMove, {
        passive: false,
        capture: true
    }), addEvent(document, 'touchend', onTouchEnd, {
        passive: false,
        capture: true
    }), addEvent(document, 'focus', onFocus, true));
    return ()=>{
        // Restore styles and scroll the page back to where it was.
        restoreScrollableStyles?.();
        restoreStyles?.();
        removeEvents();
    };
}
// Sets a CSS property on an element, and returns a function to revert it to the previous value.
function setStyle(element, style, value) {
    const cur = element.style[style];
    element.style[style] = value;
    return ()=>{
        // @ts-ignore - adapted from react-aria
        element.style[style] = cur;
    };
}
// Adds an event listener to an element, and returns a function to remove it.
function addEvent(target, event, handler, options) {
    // @ts-ignore internal function, so it's ok to ignore the difficult to fix type error
    target.addEventListener(event, handler, options);
    return ()=>{
        // @ts-ignore internal function, so it's ok to ignore the difficult to fix type error
        target.removeEventListener(event, handler, options);
    };
}
function willOpenKeyboard(target) {
    return target instanceof HTMLInputElement && !nonTextInputTypes.has(target.type) || target instanceof HTMLTextAreaElement || target instanceof HTMLElement && target.isContentEditable;
}

const LOCALES = [
    'en-GB',
    'de-DE',
    'fr-FR',
    'nl-NL'
];

const CORE_TRANSLATIONS = {
    'en-GB': {
        'core::close': 'Close'
    },
    'fr-FR': {
        'core::close': 'Fermer'
    },
    'de-DE': {
        'core::close': 'Schließen'
    },
    'nl-NL': {
        'core::close': 'Sluiten'
    }
};

function getClosestLocale(locale) {
    if (locale in LOCALES) {
        return locale;
    }
    let localeBase = locale.split('-')[0];
    if (Intl.Locale) {
        localeBase = new Intl.Locale(locale).language;
    }
    for (const key of LOCALES){
        if (key.startsWith(localeBase + '-')) {
            return key;
        }
    }
    return DEFAULT_LOCALE;
}
/**
 * Hook to get the translator function `t`
 * @param customTranslations Optional argument to define custom translations
 * @returns Translator function exposing the selected core or, when defined, custom translation
 */ const useTranslation = (customTranslations)=>{
    const locale = useLocale();
    const closestTranslationLocale = useMemo(()=>getClosestLocale(locale), [
        locale
    ]);
    const allTranslations = useMemo(()=>{
        return LOCALES.reduce((allTranslations, locale)=>{
            const coreTranslations = CORE_TRANSLATIONS[locale];
            allTranslations[locale] = customTranslations === undefined ? coreTranslations : {
                ...coreTranslations,
                ...customTranslations[locale]
            };
            return allTranslations;
        }, {});
    }, [
        customTranslations
    ]);
    return useCallback(function translator(key) {
        return allTranslations[closestTranslationLocale][key];
    }, [
        allTranslations,
        closestTranslationLocale
    ]);
};

const useHorizontalScroll = ({ scrollContainer, selectedElement, firstElement, lastElement }, { scrollThreshold = 0.5 } = {})=>{
    const [shouldDisplayLeftArrow, setShouldDisplayLeftArrow] = useState(false);
    const [shouldDisplayRightArrow, setShouldDisplayRightArrow] = useState(false);
    const [boxProps, setBoxProps] = useState({
        width: 0,
        left: 0
    });
    useResizeObserver(selectedElement, {
        onResize: ()=>{
            if (!selectedElement.current || !boxProps) return;
            if (boxProps.width === selectedElement.current.offsetWidth && boxProps.left === selectedElement.current.offsetLeft) return;
            setBoxProps({
                width: selectedElement.current.offsetWidth,
                left: selectedElement.current.offsetLeft
            });
        }
    });
    const updateArrowDisplay = useCallback(()=>{
        const current = scrollContainer.current;
        if (!current) return;
        if (firstElement.current) {
            /** Not displaying left button instantly when right scrolling */ setShouldDisplayLeftArrow(current.scrollLeft > firstElement.current?.scrollWidth * scrollThreshold);
        }
        setShouldDisplayRightArrow(current.scrollWidth - current.scrollLeft - 1 > current.clientWidth);
    }, [
        firstElement,
        scrollContainer,
        scrollThreshold
    ]);
    useEffect(()=>{
        const current = scrollContainer.current;
        updateArrowDisplay();
        if (current) {
            current.addEventListener('scroll', updateArrowDisplay);
            window.addEventListener('resize', updateArrowDisplay);
        }
        return ()=>{
            if (current) {
                current.removeEventListener('scroll', updateArrowDisplay);
            }
            window.removeEventListener('resize', updateArrowDisplay);
        };
    }, [
        scrollContainer,
        lastElement,
        updateArrowDisplay
    ]);
    return {
        shouldDisplayLeftArrow,
        shouldDisplayRightArrow,
        boxProps
    };
};

const useNotificationGroup = ()=>{
    const contextValue = useContext(NotificationContext);
    if (!contextValue) {
        throw new Error('The `NotificationProvider` has not been instantiated correctly with its corresponding context. Please make sure that `GeminiProvider` is rendered higher in your app. If the issue persists, report it to Team GEMINI.');
    }
    // Cast to allow custom content, generic on React Context is not easy
    return contextValue;
};
const useNotification = (ref, { state, ...props })=>{
    const { toastProps, contentProps: { style: contentStyle, ...restContentProps }, titleProps, descriptionProps } = useToast(props, state, ref);
    return {
        boxProps: {
            toast: toastProps,
            content: {
                unsafe_style: contentStyle,
                ...restContentProps
            },
            title: titleProps,
            description: descriptionProps
        }
    };
};

/**
 * `:root` default font-size
 */ const DEFAULT_BASE_FONT_SIZE = 16;
/**
 * Return the `:root` font-size for scaling purpose
 * ⚠️ Should be used only client side, window/document is not available server side
 * @returns {number} `:root` font-size
 */ const getRootFontSizeValue = ()=>// Can be used as a normal function, no hooks inside
    // eslint-disable-next-line react-hooks/rules-of-hooks
    !useIsServer() && parseFloat(window.getComputedStyle(document.querySelector(':root')).getPropertyValue('font-size')) || DEFAULT_BASE_FONT_SIZE;

export { Box, FocusableProvider, GeminiProvider, Icon, Illustration, Overlay, ServerProvider, UNSAFE_GEMINI_PORTAL_CLASS_SELECTOR, getRootFontSizeValue, mergeProps, useBreakpointValue, useColorScheme, useFocusable, useHorizontalScroll, useHoverable, useIsServer, useLayoutEffect, useLocale, useNotification, useNotificationGroup, useObjectRef, usePopover, usePressable, usePreventScroll, useRenderLink, useResizeObserver, useSafeArea, useTokens, useTranslation, useZIndexContext };
//# sourceMappingURL=index.mjs.map
